import { AlertTriangle, Clock3, Download, Info, LoaderCircle, RefreshCw, Send } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonIcon } from "../../../components/button";
import { ScrollableContentLayout } from "../../../components/common";
import { useToast } from "../../../components/toast-container";
import { DataTable } from "../../../components/table";
import type { DataTableColumn } from "../../../components/table";
import { getApiErrorMessage } from "../../../lib/result-codes";
import {
  useExportOutboxNotifications,
  useOutboxNotificationDetail,
  useOutboxNotifications,
} from "../hooks";
import { OutboxNotificationDetailDialog } from "../components/OutboxNotificationDetailDialog";
import {
  OUTBOX_STATUSES,
  type OutboxNotificationExport,
  type OutboxNotificationItem,
  type OutboxStatus,
} from "../types/outboxNotifications.types";

const statusClassNames: Record<OutboxStatus, string> = {
  pending: "bg-[#fff0d1] text-[#805200]",
  processing: "bg-[#dbeeff] text-[#075985]",
  sent: "bg-[#d3f4e9] text-[#005342]",
  dead: "bg-[#fee2e2] text-[#991b1b]",
};

const statusIcons: Record<OutboxStatus, ReactNode> = {
  pending: <Clock3 className="h-4 w-4" aria-hidden="true" />,
  processing: <LoaderCircle className="h-4 w-4" aria-hidden="true" />,
  sent: <Send className="h-4 w-4" aria-hidden="true" />,
  dead: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
};

const formatTimestamp = (value: string | null): string => {
  if (!value) return "—";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const downloadExport = ({ data, contentType, filename }: OutboxNotificationExport): void => {
  const url = window.URL.createObjectURL(new Blob([data], { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function OutboxNotificationsPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const {
    items,
    summary,
    pagination,
    filters,
    error,
    loading,
    refetch,
    setPage,
  } = useOutboxNotifications();
  const { exportReport, isExporting } = useExportOutboxNotifications();
  const { detail, error: detailError, loading: detailLoading, loadDetail, clearDetail } = useOutboxNotificationDetail();
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);

  const handleOpenDetail = useCallback((id: string) => {
    setSelectedDetailId(id);
    void loadDetail(id);
  }, [loadDetail]);

  const handleDetailOpenChange = useCallback((open: boolean) => {
    if (open) return;

    setSelectedDetailId(null);
    clearDetail();
  }, [clearDetail]);

  const columns = useMemo<DataTableColumn<OutboxNotificationItem>[]>(
    () => [
      {
        key: "userFullName",
        title: t("outbox_notifications.messages.columns.full_name"),
        minWidth: 180,
        render: (message) => <span className="font-medium text-[#005342]">{message.userFullName ?? "—"}</span>,
      },
      {
        key: "userEmail",
        title: t("outbox_notifications.messages.columns.email"),
        minWidth: 240,
        render: (message) => <span className="break-all text-sm text-[#587268]">{message.userEmail ?? "—"}</span>,
      },
      {
        key: "organizationName",
        title: t("outbox_notifications.messages.columns.organization"),
        minWidth: 200,
        render: (message) => <span className="text-sm text-[#587268]">{message.organizationName ?? "—"}</span>,
      },
      {
        key: "type",
        title: t("outbox_notifications.messages.columns.type"),
        minWidth: 180,
        render: (message) => (
          <div>
            <p className="font-bold text-[#005342]">{message.channel}</p>
            <p className="mt-1 text-sm text-[#587268]">{message.type}</p>
          </div>
        ),
      },
      {
        key: "status",
        title: t("outbox_notifications.messages.columns.status"),
        minWidth: 130,
        render: (message) => (
          <span className={`rounded-full px-3 py-1.5 text-sm font-bold ${statusClassNames[message.status]}`}>
            {t(`outbox_notifications.status.${message.status}`)}
          </span>
        ),
      },
      { key: "attempts", title: t("outbox_notifications.messages.columns.attempts"), minWidth: 100, render: (message) => <span className="font-semibold text-[#005342]">{message.attempts}</span> },
      { key: "nextAttemptAt", title: t("outbox_notifications.messages.columns.next_attempt"), minWidth: 165, render: (message) => <span className="text-sm text-[#587268]">{formatTimestamp(message.nextAttemptAt)}</span> },
      { key: "updatedAt", title: t("outbox_notifications.messages.columns.updated"), minWidth: 165, render: (message) => <span className="text-sm text-[#587268]">{formatTimestamp(message.updatedAt)}</span> },
      { key: "lastError", title: t("outbox_notifications.messages.columns.error"), minWidth: 220, render: (message) => <span className="text-sm text-[#805200]">{message.lastError ?? "—"}</span> },
      {
        key: "details",
        title: t("outbox_notifications.messages.columns.details"),
        minWidth: 70,
        align: "center",
        render: (message) => (
          <ButtonIcon
            aria-label={t("outbox_notifications.messages.view_details")}
            title={t("outbox_notifications.messages.view_details")}
            onClick={() => handleOpenDetail(message.id)}
          >
            <Info className="h-4 w-4" aria-hidden="true" />
          </ButtonIcon>
        ),
      },
    ],
    [handleOpenDetail, t],
  );

  const summaryByStatus = useMemo(
    () => new Map(summary.map((item) => [item.status, item])),
    [summary],
  );
  const statistics = useMemo(
    () => OUTBOX_STATUSES.map((status) => {
      const statusSummary = summaryByStatus.get(status);
      return {
        label: t(`outbox_notifications.status.${status}`),
        value: (statusSummary?.count ?? 0).toLocaleString(),
        description: statusSummary?.lastUpdatedAt
          ? t("outbox_notifications.status_summary.updated", { updated: formatTimestamp(statusSummary.lastUpdatedAt) })
          : t("outbox_notifications.status_summary.no_updates"),
        icon: statusIcons[status],
      };
    }),
    [summaryByStatus, t],
  );

  const handleExport = useCallback(async () => {
    try {
      const exportFile = await exportReport(filters);
      if (!exportFile) return;

      downloadExport(exportFile);
      addToast({
        type: "success",
        title: t("outbox_notifications.actions.export_report"),
        message: t("outbox_notifications.actions.export_success"),
      });
    } catch (requestError: unknown) {
      addToast({
        type: "error",
        title: t("outbox_notifications.actions.export_report"),
        message: getApiErrorMessage(requestError, t("outbox_notifications.actions.export_error")),
      });
    }
  }, [addToast, exportReport, filters, t]);

  return (
    <main className="min-h-full">
      <ScrollableContentLayout
        title={t("outbox_notifications.title")}
        subtitle={t("outbox_notifications.description")}
        stats={statistics}
        actions={
          <Button type="button" variant="outline" className="flex items-center gap-2" onClick={() => void handleExport()} disabled={isExporting}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t("outbox_notifications.actions.export_report")}
          </Button>
        }
        contentHeaderTitle={t("outbox_notifications.messages.title")}
        contentHeaderActions={
          <Button type="button" variant="outline" size="sm" className="flex items-center gap-2" onClick={() => void refetch()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            {t("ui.refresh")}
          </Button>
        }
        headerContent={<p className="mb-4 text-sm text-muted-foreground">{t("outbox_notifications.messages.description")}</p>}
      >
        <DataTable
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          error={error}
          onRetry={() => void refetch()}
          emptyLabel={t("outbox_notifications.messages.empty")}
          containerClassName="max-h-[calc(100vh-23rem)] overflow-auto rounded-lg border border-gray-200"
          tableClassName="min-w-[1320px]"
          pagination={{
            page: pagination.page,
            totalPages: pagination.totalPages,
            onPageChange: setPage,
          }}
        />
      </ScrollableContentLayout>
      <OutboxNotificationDetailDialog
        open={selectedDetailId !== null}
        onOpenChange={handleDetailOpenChange}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onRetry={() => {
          if (selectedDetailId) void loadDetail(selectedDetailId);
        }}
      />
    </main>
  );
}
